import { Injectable } from '@angular/core';
import { generateClient } from 'aws-amplify/api';
import * as queries from '@graphql/queries';
import * as mutations from '@graphql/mutations';
import {
    WalletMovement,
    CreateWalletMovementInput,
    UpdateWalletMovementInput,
    DeleteWalletMovementInput,
    ModelWalletMovementFilterInput,
    ModelSortDirection
} from '@app/core/services/api/API.service';

@Injectable({
    providedIn: 'root'
})
export class WalletMovementService {
    private amplifyClient = generateClient();

    constructor() {}

    async listWalletMovements(): Promise<WalletMovement[]> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: queries.listWalletMovements
                })
            ).data.listWalletMovements.items;
            if (data) return data as WalletMovement[];
        } catch (error) {
            console.error('Error finding WalletMovements:', error);
            return null;
        }
    }

    async getWalletMovement(id: string): Promise<WalletMovement> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: queries.getWalletMovement,
                    variables: {
                        id
                    }
                })
            ).data.getWalletMovement;
            if (data) return data as WalletMovement;
        } catch (error) {
            console.error('Error finding WalletMovement:', error);
            return null;
        }
    }

    async walletMovementsByUserId(
        userId: string,
        sortDirection: ModelSortDirection = ModelSortDirection.DESC,
        filter: ModelWalletMovementFilterInput = null,
        limit: number = null,
        nextToken: string = null
    ): Promise<{ items: WalletMovement[]; nextToken: string }> {
        try {
            const response = (
                await this.amplifyClient.graphql({
                    query: queries.WalletMovementsByUserId,
                    variables: {
                        userId,
                        sortDirection,
                        filter,
                        limit,
                        nextToken
                    }
                })
            ).data.WalletMovementsByUserId;
            if (response) {
                return {
                    items: response.items as WalletMovement[],
                    nextToken: response.nextToken
                };
            }
            return { items: [], nextToken: null };
        } catch (error) {
            console.error('Error finding WalletMovements by userId:', error);
            return { items: [], nextToken: null };
        }
    }

    async getAllWalletMovementsByUserId(
        userId: string,
        sortDirection: ModelSortDirection = ModelSortDirection.DESC,
        filter: ModelWalletMovementFilterInput = null
    ): Promise<WalletMovement[]> {
        let allItems: WalletMovement[] = [];
        let nextToken: string = null;

        try {
            do {
                const response = await this.walletMovementsByUserId(
                    userId,
                    sortDirection,
                    filter,
                    null,
                    nextToken
                );
                if (response.items) {
                    allItems.push(...response.items);
                }
                nextToken = response.nextToken;
            } while (nextToken !== null);

            return allItems;
        } catch (error) {
            console.error(
                'Error fetching all WalletMovements by userId:',
                error
            );
            return allItems;
        }
    }

    async createWalletMovement(
        input: CreateWalletMovementInput
    ): Promise<WalletMovement> {
        try {
            const walletMovement = (
                await this.amplifyClient.graphql({
                    query: mutations.createWalletMovement,
                    variables: {
                        input
                    }
                })
            ).data.createWalletMovement;
            if (walletMovement) return walletMovement as WalletMovement;
        } catch (error) {
            console.error('Error creating walletMovement:', error);
            return null;
        }
    }

    async updateWalletMovement(
        input: UpdateWalletMovementInput
    ): Promise<WalletMovement> {
        try {
            const walletMovement = (
                await this.amplifyClient.graphql({
                    query: mutations.updateWalletMovement,
                    variables: {
                        input
                    }
                })
            ).data.updateWalletMovement;
            if (walletMovement) return walletMovement as WalletMovement;
        } catch (error) {
            console.error('Error updating walletMovement:', error);
            return null;
        }
    }

    async deleteWalletMovement(
        input: DeleteWalletMovementInput
    ): Promise<WalletMovement> {
        try {
            const deleteWalletMovement = (
                await this.amplifyClient.graphql({
                    query: mutations.deleteWalletMovement,
                    variables: {
                        input
                    }
                })
            ).data.deleteWalletMovement;
            return deleteWalletMovement as WalletMovement;
        } catch (error) {
            console.error('Error deleting walletMovement:', error);
            return null;
        }
    }
}
